// src/redux/energyRoomSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

const initialState = {
  isOpen: false,
  floorPlanId: null,
  clipRect: null,             // { minX, minY, maxX, maxY } world coords
  panels: [],                 // [{ id, label, compartments, totalWidthCm, depthCm, x, y, rotation }]
                              //   x, y = world coords (panel center), rotation = radians
  selectedPanelId: null,
  wallClearanceCm: 10,
  panelDepthCm: 30,           // Pano derinligi (plan gorunumunde kisa kenar)
  drawingScale: 1,            // Mimari cizim olcegi (1:50 -> 50). cm / scale = world units
  dimTextSize: 4,              // Olcu text boyutu (cm)
  dimArrowSize: 3,             // Olcu ok boyutu (cm)
  dimExtInner: 4,              // Ic olcu uzanti cizgisi mesafesi (cm) - bolme olculeri
  dimExtOuter: 16,             // Dis olcu uzanti cizgisi mesafesi (cm) - toplam olcu
  dimLineSpacing: 6,           // Ic olcu cizgileri arasi mesafe (cm)
  status: 'idle',
  panelConfig: null,
  panelMeters: [],
  separateDistribution: false,
  splitParts: [],               // [4, 1] gibi bolme sutun sayilari (bos = tek pano)
  dimensionMode: false,         // Olcu araci aktif mi
  customDimensions: [],         // [{id, x1, y1, x2, y2, offset}] world coords
  customDimTextSize: 8,         // Kullanici olcu text boyutu (cm)
  customDimArrowSize: 3,        // Kullanici olcu ok boyutu (cm)
  frameMargin: { top: 10, bottom: 10, left: 10, right: 10 }, // Cerceve ofsetleri (cm)
  confirmed: false,             // Enerji odasi "Tamam" ile onaylandi mi
  clippedSegments: [],          // Kliplanmis duvar segmentleri [{x1,y1,x2,y2}]
  panoDetayConfig: null,        // generatePanoDetayV2 icin config
  energyRoomLaunchPayload: null, // Enerji Odasi'ni acmak icin guncel config (PanoDetayPanel sync eder, Tesisat butonu kullanir)
};

const energyRoomSlice = createSlice({
  name: 'energyRoom',
  initialState,
  reducers: {
    openEnergyRoom(state, action) {
      state.isOpen = true;
      state.floorPlanId = action.payload?.floorPlanId ?? null;
      state.panelConfig = action.payload?.config ?? null;
      state.panelMeters = action.payload?.meters ?? [];
      state.separateDistribution = action.payload?.separateDistribution ?? false;
      state.splitParts = action.payload?.splitParts ?? [];
      state.clipRect = null;
      state.panels = [];
      state.selectedPanelId = null;
      state.status = 'idle';
    },
    closeEnergyRoom(state) {
      state.isOpen = false;
      state.floorPlanId = null;
      state.clipRect = null;
      state.panels = [];
      state.selectedPanelId = null;
      state.status = 'idle';
      state.panelConfig = null;
      state.panelMeters = [];
      state.separateDistribution = false;
      state.splitParts = [];
      state.dimensionMode = false;
      state.customDimensions = [];
      state.confirmed = false;
      state.clippedSegments = [];
      state.panoDetayConfig = null;
    },
    confirmEnergyRoom(state) {
      state.confirmed = true;
      state.isOpen = false;
    },
    setClipRect(state, action) {
      state.clipRect = action.payload;
      state.status = 'clipped';
    },
    addPanel(state, action) {
      const panel = { ...action.payload };
      if (panel.rotation === undefined) panel.rotation = 0;
      state.panels.push(panel);
      state.status = 'placing';
    },
    updatePanelPosition(state, action) {
      const { id, x, y, rotation } = action.payload;
      const panel = state.panels.find(p => p.id === id);
      if (panel) {
        panel.x = x;
        panel.y = y;
        if (rotation !== undefined) panel.rotation = rotation;
      }
    },
    removePanel(state, action) {
      state.panels = state.panels.filter(p => p.id !== action.payload);
      if (state.selectedPanelId === action.payload) state.selectedPanelId = null;
    },
    setSelectedPanel(state, action) { state.selectedPanelId = action.payload; },
    setStatus(state, action) { state.status = action.payload; },
    setWallClearance(state, action) { state.wallClearanceCm = action.payload; },
    setPanelDepth(state, action) { state.panelDepthCm = action.payload; },
    setDrawingScale(state, action) { state.drawingScale = action.payload; },
    setDimTextSize(state, action) { state.dimTextSize = action.payload; },
    setDimArrowSize(state, action) { state.dimArrowSize = action.payload; },
    setDimExtInner(state, action) { state.dimExtInner = action.payload; },
    setDimExtOuter(state, action) { state.dimExtOuter = action.payload; },
    setDimLineSpacing(state, action) { state.dimLineSpacing = action.payload; },
    resetRoom(state) {
      state.clipRect = null;
      state.panels = [];
      state.selectedPanelId = null;
      state.status = 'idle';
      state.dimensionMode = false;
      state.customDimensions = [];
      state.clippedSegments = [];
    },
    setDimensionMode(state, action) {
      state.dimensionMode = action.payload;
    },
    addCustomDimension(state, action) {
      state.customDimensions.push(action.payload);
    },
    removeCustomDimension(state, action) {
      state.customDimensions = state.customDimensions.filter(d => d.id !== action.payload);
    },
    clearCustomDimensions(state) {
      state.customDimensions = [];
    },
    setCustomDimTextSize(state, action) { state.customDimTextSize = action.payload; },
    setCustomDimArrowSize(state, action) { state.customDimArrowSize = action.payload; },
    setFrameMargin(state, action) {
      // action.payload = { top?, bottom?, left?, right? } partial update
      Object.assign(state.frameMargin, action.payload);
    },
    setClippedSegments(state, action) {
      state.clippedSegments = action.payload;
    },
    setPanoDetayConfig(state, action) {
      state.panoDetayConfig = action.payload;
    },
    setEnergyRoomLaunchPayload(state, action) {
      state.energyRoomLaunchPayload = action.payload;
    },
  },
});

export const {
  openEnergyRoom, closeEnergyRoom, confirmEnergyRoom, setClipRect,
  addPanel, updatePanelPosition, removePanel,
  setSelectedPanel, setStatus,
  setWallClearance, setPanelDepth, setDrawingScale,
  setDimTextSize, setDimArrowSize, setDimExtInner, setDimExtOuter, setDimLineSpacing,
  resetRoom,
  setDimensionMode, addCustomDimension, removeCustomDimension,
  clearCustomDimensions,
  setCustomDimTextSize, setCustomDimArrowSize,
  setFrameMargin,
  setClippedSegments, setPanoDetayConfig, setEnergyRoomLaunchPayload,
} = energyRoomSlice.actions;

export const selectEnergyRoomState = (state) => state.energyRoom;
export const selectIsEnergyRoomOpen = (state) => state.energyRoom.isOpen;
export const selectClipRect = (state) => state.energyRoom.clipRect;
export const selectPanels = (state) => state.energyRoom.panels;
export const selectEnergyRoomStatus = (state) => state.energyRoom.status;
export const selectSelectedPanelId = (state) => state.energyRoom.selectedPanelId;
export const selectWallClearance = (state) => state.energyRoom.wallClearanceCm;
export const selectPanelDepth = (state) => state.energyRoom.panelDepthCm;
export const selectDrawingScale = (state) => state.energyRoom.drawingScale;
export const selectDimSettings = createSelector(
  (state) => state.energyRoom.dimTextSize,
  (state) => state.energyRoom.dimArrowSize,
  (state) => state.energyRoom.dimExtInner,
  (state) => state.energyRoom.dimExtOuter,
  (state) => state.energyRoom.dimLineSpacing,
  (textSize, arrowSize, extInner, extOuter, lineSpacing) => ({
    textSize, arrowSize, extInner, extOuter, lineSpacing,
  })
);
export const selectPanelConfig = (state) => state.energyRoom.panelConfig;
export const selectPanelMeters = (state) => state.energyRoom.panelMeters;
export const selectSeparateDistribution = (state) => state.energyRoom.separateDistribution;
export const selectSplitParts = (state) => state.energyRoom.splitParts;
export const selectDimensionMode = (state) => state.energyRoom.dimensionMode;
export const selectCustomDimensions = (state) => state.energyRoom.customDimensions;
export const selectFrameMargin = (state) => state.energyRoom.frameMargin;
export const selectCustomDimSettings = createSelector(
  (state) => state.energyRoom.customDimTextSize,
  (state) => state.energyRoom.customDimArrowSize,
  (textSize, arrowSize) => ({ textSize, arrowSize })
);
export const selectEnergyRoomConfirmed = (state) => state.energyRoom.confirmed;
export const selectClippedSegments = (state) => state.energyRoom.clippedSegments;
export const selectPanoDetayConfig = (state) => state.energyRoom.panoDetayConfig;
export const selectEnergyRoomLaunchPayload = (state) => state.energyRoom.energyRoomLaunchPayload;

export default energyRoomSlice.reducer;
